import { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { CircleAlert } from 'lucide-react';
import { cn } from '@hqo/react-hooks';
import { ActivityAction, ActivityItem, ActivitySegment } from 'app/data/types/activity';
import { ActivityFilter, NON_RECORD_ACTIONS } from 'app/shared/consts/activity';
import { CTA_TYPES, OPEN_MODES } from 'app/shared/consts';
import { useActivityFeedQuery, useActivityFeedStream, useRefetchActivityFeed } from 'app/data/queries/use-activity-feed-query';
import { useActivityDeeplink } from 'app/shared/hooks/use-activity-deeplink';
import { useOpenParams } from 'app/shared/hooks/use-open-params';
import { useForgetSelection, useRestoreSelection } from 'app/shared/hooks/use-selection-memory';
import { toActivityIdentity, toDestinationRoute, useActivityTracking } from 'app/shared/hooks/use-activity-tracking';
import { useActivityTarget } from 'app/shared/hooks/use-activity-target';
import { usePageImpression } from 'app/shared/hooks/use-page-impression';
import { useInfiniteScroll } from 'app/shared/hooks/use-infinite-scroll';
import { usePullGesture } from 'app/shared/hooks/use-pull-gesture';
import { INDICATOR_HEIGHT, PullIndicator } from 'app/components/pull-indicator';
import { useBookAgain } from 'app/shared/hooks/use-book-again';
import { useCancelVisitMutation } from 'app/data/mutations/use-cancel-visit-mutation';
import { formatTimeRangeLabel, formatWhen } from 'app/shared/utils/format-activity';
import { Spinner } from 'app/components/ui/spinner';
import { ActivitySegmented } from 'app/components/activity-segmented';
import { ActivityFilterChips } from 'app/components/activity-filter-chips';
import { ActivityEmptyState, EmptyStateShell } from 'app/components/activity-empty-state';
import { ActivityEndCta } from 'app/components/activity-end-cta';
import { ActivityCard } from 'app/components/activity-card';
import { VisitCancelModal } from 'app/components/visit-cancel-modal';
import { useCheckInMutation } from 'app/data/mutations/use-check-in-mutation';
import { useCancelEventBookingMutation } from 'app/data/mutations/use-cancel-event-booking-mutation';
import { useCancelResourceBookingMutation } from 'app/data/mutations/use-cancel-resource-booking-mutation';
import { useCheckInBookingMutation } from 'app/data/mutations/use-check-in-booking-mutation';
import { EventCancelModal } from 'app/components/event-cancel-modal';
import { BookingCancelModal } from 'app/components/booking-cancel-modal';
import { isBookingCancelOpen, isCancelOpen } from 'app/shared/utils/cancel-open';
import { isBookingCheckInOpen, isCheckInOpen } from 'app/shared/utils/check-in-open';
import { RatingCommentModal } from 'app/components/rating-comment-modal';
import { useFeedback } from 'app/shared/contexts/feedback';
import { useBuildingVerticals } from 'app/shared/hooks/use-building-verticals';
import { useCheckInFeedback } from 'app/shared/hooks/use-check-in-feedback';
import { useActivityFilter } from './hooks/use-activity-filter';
import { useRestoreFeedView } from 'app/shared/hooks/use-feed-view-memory';
import { useSettleAfterWrite } from './hooks/use-settle-after-write';

/** Filters whose module has a creation flow, so a populated pane can offer the create FAB. */

/** How long a pull's confirmation message stays open after release. */
const CONFIRMATION_MS = 1600;

/**
 * One phase per pull edge. `running` = the action is in flight (spinner); `done` = held open showing its
 * confirmation. Single-valued deliberately: a pair of booleans leaves gaps where neither is set, and the
 * indicator collapses and re-opens across them.
 */
type PullPhase = 'idle' | 'running' | 'done';

/**
 * A visit is cancellable while it is still expected — invited, not yet arrived or terminal — AND its
 * window has not passed. The status alone is not enough: a visitor who simply never turned up stays
 * `expected` forever, so a visit whose day is over would keep offering to cancel something that is
 * already over. The segment is the endpoint's own statement of whether it has ended, so it decides
 * rather than a second clock here.
 */
const isVisitCancelable = (item: ActivityItem, segment: ActivitySegment): boolean =>
  segment === 'upcoming' && item.type === 'visitor_registration' && item.statusKey === 'expected';

/** The event a booking belongs to — service-booking scopes check-in under it. */
const checkInEventUuid = (item: ActivityItem): string | undefined =>
  item.actions?.find((a) => a.type === 'open_record')?.routingDetails?.eventUuid;

/** The transaction a resource booking rides on — its cancel endpoint is keyed by transaction. */
const bookingTransactionUuid = (item: ActivityItem): string | undefined =>
  item.actions?.find((a) => a.type === 'open_record')?.routingDetails?.transactionUuid;

/**
 * The transaction's NUMERIC id, which is what cancelling keys on. Sent as a string like every routing
 * id; absent on documents projected before helix published it, where the mutation falls back to a
 * lookup rather than being unable to cancel at all.
 */
const bookingTransactionId = (item: ActivityItem): number | undefined => {
  const raw = item.actions?.find((a) => a.type === 'open_record')?.routingDetails?.transactionId;
  const parsed = raw ? Number(raw) : NaN;

  return Number.isInteger(parsed) ? parsed : undefined;
};

/** The routing ids the cancel endpoint needs, from the visit's open_record action. */
const visitRouting = (item: ActivityItem): { visitId?: string; buildingUuid?: string } => {
  const details = item.actions?.find((a) => a.type === 'open_record')?.routingDetails;
  return { visitId: details?.visitUuid, buildingUuid: details?.buildingUuid };
};

/**
 * The Activity Center feed: Upcoming / Completed segmented control, a category filter rail, and the
 * ordered card list (Upcoming = closest activities first, open-ended items last; Completed = most
 * recent first). Tapping a resource-booking or event TILE opens the in-app detail; every other
 * tile/CTA deeplinks OUT to the source module.
 */
/**
 * The content column. The page is full-bleed — the scroll container and its scrollbar stay at the
 * window edge — and only the content inside it is capped and centred, the same shape miniapp-content
 * uses.
 *
 * 864px is the width the host's own iframe wrapper and miniapp-content already cap at, and from `md`
 * the inset is 28px to match what that wrapper puts before its breadcrumb (a 16px gutter plus the
 * back button's own 12px). So the title lands exactly on the breadcrumb chevron above it, and it
 * holds at any width rather than only at 864, because both sides narrow together. Below `md` the
 * host hides the breadcrumb entirely, so the app keeps its own tighter insets there.
 */
const CONTENT_COLUMN = 'mx-auto w-full max-w-[864px]';
/*
 * ...except where the HOST has already bounded the width. On the portal's full-page surface this
 * app is one column of a split, sized to leave room for the record beside it — so capping again at
 * 864 and centring what is left strands the list in the middle of its own column, indented past
 * the breadcrumb it is supposed to line up with. The column is the constraint there; the app just
 * fills it.
 */
const SPLIT_CONTENT_COLUMN = 'w-full';

const HEADER_INSET = 'px-5 md:px-7';
const LIST_INSET = 'px-4 md:px-7';

export const ActivityFeed = (): JSX.Element => {
  const intl = useIntl();
  const {
    feed,
    isFetching,
    isError,
    hasPartialError,
    upcomingTotal,
    loadMore,
    canLoadMore,
    isAtEnd,
    isLoadingMore,
    refresh,
    isRefreshing,
  } = useActivityFeedQuery();
  // Hold one SSE connection to search-service so a change (booking / visit / survey / note / work order /
  // access) refetches the feed the moment it lands, instead of the feed re-requesting on every focus.
  useActivityFeedStream();
  // Seeds the URL from the last-used view on a cold start; a URL that already names one wins.
  useRestoreFeedView();
  const filterState = useActivityFilter(feed);
  const { segment, setSegment, filter, setFilter, items } = filterState;
  const {
    canOpenModule, openModule, getModuleUrl, canOpenHostPage, openHostPage, rememberInAppRecord,
  } = useActivityDeeplink();
  /*
   * On the portal's own activity route a tap opens the record BESIDE this list instead of
   * navigating away, so the list has to say which one is open. Every other surface leaves the page,
   * where a highlight would only flash on the way out — hence the surface check rather than always
   * tracking it.
   */
  const { hostSurface, buildingUuid: hostBuildingUuid, locale } = useOpenParams();
  const isSplitSurface = hostSurface === 'fullscreen';
  const contentColumn = isSplitSurface ? SPLIT_CONTENT_COLUMN : CONTENT_COLUMN;
  /*
   * The selection is kept WITH the scope it was made in, and read back only when the scope still
   * matches — rather than as a bare id that outlives the thing it referred to.
   *
   * Surface, building and language each change what this list is showing, so a highlight made under
   * one of them means nothing under the next; kept bare, a stale id would also win over a freshly
   * restored one through the `??` below. Derived rather than cleared in an effect, so there is no
   * render where the old highlight is still painted.
   *
   * Defensive rather than a live fix: all three arrive as launch params, and the host changes them
   * by re-pointing the iframe, which remounts this component and resets the state anyway. It is the
   * invariant that matters — nothing here should depend on that remount continuing to happen.
   */
  const selectionScope = `${hostSurface ?? ''}|${hostBuildingUuid ?? ''}|${locale ?? ''}`;

  /*
   * Showing the LIST means nothing is open, so nothing should be remembered as open.
   *
   * The popover's stored selection is written when a record opens on its in-app screen, and this
   * is the other half of that: coming back to the list closes the record, so the entry describing
   * it has to go. Without this the entry outlives what it describes for the whole TTL, and a
   * reader who browsed for a minute and then expanded to full screen arrived with some earlier
   * booking already filling the pane — which is what the feedback was about.
   *
   * It also means the full-screen button needs no special handling, which is just as well: it is
   * the portal's chrome, and the portal cannot see whether a record is open in here. Expanding
   * from a booking still carries it, because on that route this list is not mounted at all.
   *
   * Popover only. On the split surface the list and the pane are on screen TOGETHER, so the list
   * being visible says nothing about whether a record is open — clearing there would throw away
   * the highlight the pane depends on.
   */
  const forgetSelection = useForgetSelection(hostSurface === 'popover');
  useEffect(() => {
    forgetSelection();
  }, [forgetSelection]);
  const [selection, setSelection] = useState<{ scope: string; id: string } | null>(null);
  const selectedId = selection?.scope === selectionScope ? selection.id : null;
  /*
   * A reload keeps the host's pane — the record is in its url — but not this list's highlight,
   * which lives in the state above. Restoring it leaves the two agreeing again.
   *
   * Only ever falls back: a tap during the read wins, because it is the reader saying so now.
   */
  const restored = useRestoreSelection(hostBuildingUuid, isSplitSurface);
  /*
   * A nomination is NOT a highlight until it actually opens.
   *
   * The `command` case is a restore: the pane already showed that record, so showing the row as
   * current again is the whole point. A nomination is the other way round — it is a request to open
   * something, and it can legitimately be refused (a cancelled booking the pane cannot render).
   * Falling back to it would mark a row `aria-current` with an empty pane beside it. Once it does
   * open, `handleOpen` sets `selectedId` and the highlight arrives the ordinary way.
   */
  const highlightedId = selectedId ?? (restored?.needsOpening ? null : (restored?.itemId ?? null));
  const { iconFor, isAvailable, createTargetFor } = useBuildingVerticals();
  const { resolveTarget } = useActivityTarget();
  const { canBookAgain } = useBookAgain();
  const cancelMutation = useCancelVisitMutation();
  const [cancelItem, setCancelItem] = useState<ActivityItem | null>(null);
  /** The item being checked in — the tile whose CTA shows the in-flight state. */
  const checkInMutation = useCheckInMutation();
  /** The event booking awaiting cancel confirmation. */
  const [cancelEventItem, setCancelEventItem] = useState<ActivityItem | null>(null);
  const cancelEventMutation = useCancelEventBookingMutation();
  /** The resource booking awaiting cancel confirmation. */
  const [cancelBookingItem, setCancelBookingItem] = useState<ActivityItem | null>(null);
  const cancelBookingMutation = useCancelResourceBookingMutation();
  /** The booking being checked into — the tile whose CTA shows the in-flight state. */
  const checkInBookingMutation = useCheckInBookingMutation();
  const refetchFeed = useRefetchActivityFeed();

  const settleAfterWrite = useSettleAfterWrite(feed);
  /**
   * How a check-in reports back, for BOTH flows: a confirm beat on the tile's own button, or a toast
   * saying it didn't happen. One instance, keyed by item, rather than a copy per flow.
   */
  const checkInFeedback = useCheckInFeedback();

  /** A rating awaiting its optional comment — set only where the building collects comments. */
  const [pendingRating, setPendingRating] = useState<{ item: ActivityItem; rating: number } | null>(null);
  const { feedbackFor, ratingFor, isSubmitted, submit } = useFeedback();
  const tracking = useActivityTracking();

  // A new impression per pane, gated on the pane having actually settled — with `keepPreviousData` the
  // previous list stays on screen while a switch refetches, and an impression should describe what the
  // user can see, not what was requested.
  usePageImpression({ slugs: { segment, filter }, enabled: !isFetching && !isError });

  const handleCancel = useCallback(
    (item: ActivityItem) => {
      // The tap that OPENS the modal; the confirm is tracked separately, so the gap between them is
      // modal abandonment.
      tracking.trackTileCta({ item, ctaType: CTA_TYPES.CANCEL, openMode: OPEN_MODES.MODAL });
      setCancelItem(item);
    },
    [tracking],
  );
  const closeCancel = useCallback(() => {
    if (cancelMutation.isPending) return; // don't dismiss mid-request
    setCancelItem(null);
    cancelMutation.reset();
  }, [cancelMutation]);
  const confirmCancel = useCallback(() => {
    if (!cancelItem) return;
    const { visitId, buildingUuid } = visitRouting(cancelItem);
    if (!visitId || !buildingUuid) return;
    tracking.trackCancelSubmit(toActivityIdentity(cancelItem));
    cancelMutation.mutate(
      { visitId, buildingUuid },
      {
        onSuccess: () => {
          setCancelItem(null);
          settleAfterWrite({
            id: cancelItem.id,
            isStale: (candidate) => isVisitCancelable(candidate, segment),
            refetch: refetchFeed,
          });
        },
      },
    );
  }, [cancelItem, cancelMutation, refetchFeed, segment, settleAfterWrite, tracking]);

  const handleCheckIn = useCallback(
    (item: ActivityItem) => {
      const eventUuid = checkInEventUuid(item);
      // `isPending` is redundant with the button's disabled state — kept as cheap insurance for any
      // future caller that is not the footer button.
      if (!eventUuid || checkInMutation.isPending) return;

      // The only check-in event. What happened next is readable from the events that follow, and a
      // second submit event here would just duplicate this one's identity.
      //
      // `MODAL` outlives the modal it was named for: the open mode decides `click_type`, and anything
      // other than MODAL is reported as a NAVIGATION — which the schema then requires a
      // `destination_route` for. Checking in navigates nowhere, so the alternative gets the event
      // rejected outright. Changing it is a tracking-schema change, not a UI one.
      tracking.trackTileCta({ item, ctaType: CTA_TYPES.CHECK_IN, openMode: OPEN_MODES.MODAL });

      // The activity uuid IS the booking uuid — SBS keys the projection on it.
      checkInMutation.mutate(
        { eventUuid, bookingUuid: item.id },
        {
          onSuccess: () => {
            // The confirm beat runs on its own clock; the settle below only decides when the tile's
            // status pill catches up. Same gate the button uses, so it stops offering the moment the
            // check-in lands.
            checkInFeedback.confirm(item.id);
            settleAfterWrite({
              id: item.id,
              isStale: (candidate) => isCheckInOpen(candidate),
              refetch: refetchFeed,
            });
          },
          // The service re-validates the window and the config, so a rejection means something changed
          // since the tile was drawn — worth saying rather than leaving the button apparently inert.
          // No refetch: the notice asks the user to try again, so the button has to stay.
          onError: () => checkInFeedback.fail(item.id),
        },
      );
    },
    [checkInFeedback, checkInMutation, refetchFeed, settleAfterWrite, tracking],
  );

  const handleCancelEvent = useCallback(
    (item: ActivityItem) => {
      // Opening the modal is the tracked action; the confirm is tracked separately, so the gap
      // between them is modal abandonment — same shape as the visitor cancel.
      tracking.trackTileCta({ item, ctaType: CTA_TYPES.CANCEL, openMode: OPEN_MODES.MODAL });
      setCancelEventItem(item);
    },
    [tracking],
  );

  const closeCancelEvent = useCallback(() => {
    if (cancelEventMutation.isPending) return; // don't dismiss mid-request
    setCancelEventItem(null);
    cancelEventMutation.reset();
    // The projection lags the write, so refetch on dismiss rather than on success.
    refetchFeed();
  }, [cancelEventMutation, refetchFeed]);

  const confirmCancelEvent = useCallback(() => {
    if (!cancelEventItem) return;
    const eventUuid = checkInEventUuid(cancelEventItem);
    if (!eventUuid) return;

    tracking.trackCancelSubmit(toActivityIdentity(cancelEventItem));
    // The activity uuid IS the booking uuid — SBS keys the projection on it.
    cancelEventMutation.mutate(
      { eventUuid, bookingUuid: cancelEventItem.id },
      {
        onSuccess: () => {
          setCancelEventItem(null);
          // `isCancelOpen` is the same gate the footer button uses, so it stops reading stale the moment
          // the booking lands as cancelled.
          settleAfterWrite({ id: cancelEventItem.id, isStale: (item) => isCancelOpen(item), refetch: refetchFeed });
        },
      },
    );
  }, [cancelEventItem, cancelEventMutation, refetchFeed, settleAfterWrite, tracking]);

  const handleCancelBooking = useCallback(
    (item: ActivityItem) => {
      tracking.trackTileCta({ item, ctaType: CTA_TYPES.CANCEL, openMode: OPEN_MODES.MODAL });
      setCancelBookingItem(item);
    },
    [tracking],
  );

  const closeCancelBooking = useCallback(() => {
    if (cancelBookingMutation.isPending) return; // don't dismiss mid-request
    setCancelBookingItem(null);
    cancelBookingMutation.reset();
    // The projection lags the write, so refetch on dismiss rather than on success.
    refetchFeed();
  }, [cancelBookingMutation, refetchFeed]);

  const confirmCancelBooking = useCallback(() => {
    if (!cancelBookingItem) return;
    const transactionUuid = bookingTransactionUuid(cancelBookingItem);
    if (!transactionUuid) return;

    tracking.trackCancelSubmit(toActivityIdentity(cancelBookingItem));
    cancelBookingMutation.mutate({ transactionUuid, transactionId: bookingTransactionId(cancelBookingItem) }, {
      onSuccess: () => {
        setCancelBookingItem(null);
        // Same gate the footer button uses, so the button stops reading stale the moment the
        // cancellation lands.
        settleAfterWrite({
          id: cancelBookingItem.id,
          isStale: (item) => isBookingCancelOpen(item),
          refetch: refetchFeed,
        });
      },
    });
  }, [cancelBookingItem, cancelBookingMutation, refetchFeed, settleAfterWrite, tracking]);

  const handleCheckInBooking = useCallback(
    (item: ActivityItem) => {
      const transactionUuid = bookingTransactionUuid(item);
      if (!transactionUuid) return;
      // Global, not per-tile: the in-flight item is single-valued, so a second tap on another card
      // would replace it and let the FIRST request's success clear the second card's pending state —
      // re-enabling a button whose request is still running, and inviting a double submit.
      if (checkInBookingMutation.isPending) return;

      // `MODAL`, not `IN_APP`: the open mode decides `click_type`, and anything other than MODAL is
      // reported as a NAVIGATION — which the schema then requires a `destination_route` for. Checking
      // in navigates nowhere, so `IN_APP` sent `click_type: navigation` with a null destination and
      // the event was rejected outright. This is an action, and it matches the event check-in above.
      tracking.trackTileCta({ item, ctaType: CTA_TYPES.CHECK_IN, openMode: OPEN_MODES.MODAL });
      checkInBookingMutation.mutate(transactionUuid, {
        onSuccess: () => {
          // The confirm beat is local and fixed-length; the settle only decides when the tile's status
          // pill catches up. Same gate the button uses, so it stops offering the moment it lands.
          checkInFeedback.confirm(item.id);
          settleAfterWrite({
            id: item.id,
            isStale: (candidate) => isBookingCheckInOpen(candidate),
            refetch: refetchFeed,
          });
        },
        // The service re-validates the window and the config, so a rejection means something changed
        // since the tile was drawn — worth saying rather than leaving the button apparently inert.
        // No refetch: the notice asks the user to try again, so the button has to stay.
        onError: () => checkInFeedback.fail(item.id),
      });
    },
    [checkInBookingMutation, checkInFeedback, refetchFeed, settleAfterWrite, tracking],
  );

  /** Modal "when" for an item — same building-tz/user-locale formatting the card uses. */
  const modalWhen = useCallback(
    (item: ActivityItem | null): string => {
      if (!item?.timestamp) return '';
      const w = formatWhen(item.timestamp, item.context.buildingTimezone, undefined, intl.locale);
      const day = w.dayKey === 'date' ? w.dateLabel : intl.formatMessage({ id: `activity.day.${w.dayKey}` });
      const time = item.endTimestamp
        ? formatTimeRangeLabel(item.timestamp, item.endTimestamp, item.context.buildingTimezone, intl.locale)
        : w.timeLabel;
      return `${day} · ${time}`;
    },
    [intl],
  );

  // Modal "when" — same building-tz/user-locale range the card shows.
  let cancelWhen = '';
  if (cancelItem?.timestamp) {
    const w = formatWhen(cancelItem.timestamp, cancelItem.context.buildingTimezone, undefined, intl.locale);
    const day = w.dayKey === 'date' ? w.dateLabel : intl.formatMessage({ id: `activity.day.${w.dayKey}` });
    const time = cancelItem.endTimestamp
      ? formatTimeRangeLabel(
        cancelItem.timestamp,
        cancelItem.endTimestamp,
        cancelItem.context.buildingTimezone,
        intl.locale,
      )
      : w.timeLabel;
    cancelWhen = `${day} · ${time}`;
  }

  // One resolver decides how a tap opens AND performs it, so the telemetry payload can never claim a
  // mode the navigation didn't take. Adding an in-app CTA later needs no tracking change.
  const handleOpen = useCallback(
    (item: ActivityItem, action?: ActivityAction) => {
      const target = resolveTarget(item, action);
      if (!target) return; // unroutable → no navigation and no event, rather than a dead tap

      /*
       * Set before the open, not after it resolves: the host decides where the record goes, and the
       * list's job is only to show which row the reader asked for.
       *
       * Any action that opens the RECORD moves it, not just a bare tile tap — "Approve estimate"
       * opens the work order in the pane, and gating this on `!action` left the previous row
       * highlighted and `aria-current` while the pane showed a different record. `book_again` is
       * excluded because it opens a creation flow, which is not a record and does not use the pane.
       */
      if (isSplitSurface && !NON_RECORD_ACTIONS.has(action?.type ?? '')) {
        setSelection({ scope: selectionScope, id: item.id });
      }

      /*
       * The popover's counterpart to the selection the split surface stores when it hands a record
       * to the host. This is the one tap off that surface which lands on a record and STAYS on it,
       * and the popover can be expanded to full screen — a page that reopens the last remembered
       * record. Without this it reopens whatever an earlier visit left instead of the booking on
       * screen.
       *
       * Every record the in-app screen opens is now also one the pane can draw, so there is
       * nothing left to exclude here — the receipt renders past and cancelled bookings, and a
       * finished event opens its own page. A no-op off the popover regardless: the writer itself
       * is gated, so the phone hosts, with no pane to expand into, record nothing.
       */
      if (target.mode === OPEN_MODES.IN_APP) {
        rememberInAppRecord(item);
      }

      const { mode: openMode, destinationRoute } = target;
      if (action) {
        tracking.trackTileCta({ item, ctaType: action.type, openMode, destinationRoute });
      } else {
        tracking.trackTile({ item, openMode, destinationRoute });
      }

      target.open();
    },
    [resolveTarget, tracking, isSplitSurface, selectionScope, rememberInAppRecord],
  );

  /*
   * A row nominated by the host, opened as though it had been tapped here.
   *
   * The portal's home page links its activity rows to this page rather than into each vertical, and
   * says which record it meant by leaving the id in host storage. It cannot leave a url: building
   * those per vertical is this app's job, and a second copy on that side would drift. So the
   * nomination arrives bare and is turned into a real open here, which also rewrites the stored
   * entry with the command actually sent.
   *
   * Once per id. `handleOpen` is what a tap runs, so this inherits its routing, its telemetry and
   * its highlight rather than reproducing any of them. An id the current list does not hold — a
   * record on the other segment, or a feed still loading — simply does not fire; the page then
   * shows its empty pane, which is the honest answer to "we could not find that one".
   */
  // Tappable === routable, so ask the same resolver that will handle the tap rather than re-deriving it.
  /**
   * A FINISHED EVENT keeps its tap, and opens its event page like any other.
   *
   * It used to be excluded, on the grounds that the events miniapp "does not keep a finished event
   * findable". That is true of the places an event is normally DISCOVERED — the content views list
   * upcoming events only — but not of the event page itself, which is fetched by uuid and has no
   * date filter. Being unreachable by browsing is not the same as being unreachable by link, and
   * the tile holds the link.
   *
   * What made the exclusion look right was the state of that page, not its existence: a page that
   * offered Book or Cancel on something already over. It no longer does — a terminal event resolves
   * every CTA to none and renders its own notice, and an ended session keeps only the way back to
   * the event's schedule. So the tile can lead there and the destination behaves.
   *
   * Cancelled bookings stay in Upcoming until their event ends, which is exactly when someone
   * might still want to look at what they cancelled — and they now open there too.
   *
   * There is no surface-specific exclusion left. The split pane used to refuse a booking that was
   * cancelled or over, because the resource-booking receipt resolved its uuid out of the
   * transactions LIST and that endpoint is scoped `is_upcoming=true` — a terminal booking was
   * never in it, so the pane sat on a loading placeholder. The receipt reads the single
   * transaction now, which carries no such scope, and renders both. The last exclusion went with
   * it, so tappable is once again exactly routable.
   */
  const isTappable = useCallback(
    (item: ActivityItem): boolean => resolveTarget(item) !== null,
    [resolveTarget],
  );

  /*
   * A row nominated by the host, opened as though it had been tapped here.
   *
   * The portal's home page links its activity rows to this page rather than into each vertical, and
   * says which record it meant by leaving the id in host storage. It cannot leave a url: building
   * those per vertical is this app's job, and a second copy on that side would drift. So the
   * nomination arrives bare and is turned into a real open here, which also rewrites the stored
   * entry with the command actually sent.
   *
   * Once per id. `handleOpen` is what a tap runs, so this inherits its routing, its telemetry and
   * its highlight rather than reproducing any of them. An id the current list does not hold — a
   * record on the other segment, or a feed still loading — simply does not fire; the page then
   * shows its empty pane, which is the honest answer to "we could not find that one".
   *
   * Still asks `isTappable` rather than calling `handleOpen` straight: a nomination must not open
   * what a finger could not. The two agree exactly today — every pane-specific exclusion has now
   * been lifted, so `isTappable` is just routability — but that is a fact about the destinations,
   * not a property of this code, and it has changed three times already. Asking the same question
   * a tap asks keeps them in step whenever it changes again.
   *
   * `selectedId` holds the reader ahead of the nomination. The storage read is asynchronous, so a
   * reader can open something while it is still out; arriving late is not a reason to take the pane
   * off them.
   *
   * Split surfaces only, and that is a decision rather than an oversight: a nomination exists to
   * fill the PANE, and the mobile hosts show one screen at a time. Activating there would pull a
   * reader out of the feed they just opened and into a record they never tapped. The host still
   * writes the feed VIEW, so the list does land on the right filter — it just opens nothing.
   */
  const activatedNomination = useRef<string | null>(null);
  useEffect(() => {
    if (!isSplitSurface || !restored?.needsOpening || selectedId) return;
    if (activatedNomination.current === restored.itemId) return;

    const nominated = items.find(({ id }) => id === restored.itemId);
    if (!nominated || !isTappable(nominated)) return;

    activatedNomination.current = restored.itemId;
    handleOpen(nominated);
  }, [isSplitSurface, restored, items, handleOpen, isTappable, selectedId]);

  // Opens the active vertical's creation flow — shared by the empty-state CTA and the create FAB. The two
  // stay separate events so we can tell creating-from-empty apart from creating with items already listed.
  /*
   * Three destinations, because the three questions differ.
   *
   * `all` and Events ask the HOST for one of its own pages: a reader who has run out of everything
   * wants the building, and one who has run out of events wants the events page — neither is a
   * creation flow, and the events miniapp is not the events page. Everything else opens its
   * module's create flow, which is what its label promises.
   *
   * Both host-page routes are available on the tenant portal only, and neither has a fallback: the
   * events miniapp has no list route — its base is the legacy service-booking home, which is a
   * different app's landing page rather than events — and the mobile hosts' `NativeScreens`
   * vocabulary (AMENITIES, COMPANIES, COMMUNITY_FORUM, OFFICE_CAPACITY, LIVESAFE, CONCIERGE,
   * ORDER_AHEAD, SECONDARY_CONTENT, CONTENT) names neither a home nor an events screen to ask for.
   * So off the portal both offer nothing at all; `showCta` withholds the button rather than
   * pointing it somewhere that is not what it says.
   */
  const openCreateFlow = useCallback((): void => {
    if (filter === 'all' || filter === 'event') {
      if (canOpenHostPage) openHostPage(filter === 'all' ? 'HOME' : 'EVENTS');
      return;
    }

    // VIS-2189: the CTA goes wherever the building's own button for the vertical goes.
    openModule(filter, { target: createTargetFor(filter) });
  }, [filter, openModule, openHostPage, canOpenHostPage, createTargetFor]);

  // Both CTAs land on the module's creation flow, which `getModuleUrl` already knows — the same url
  // `openCreateFlow` is about to open. A host page has no url to record, so it records none.
  const createDestination = useCallback(
    (): string | null => (filter === 'all' || filter === 'event'
      ? null
      : toDestinationRoute(getModuleUrl(filter, { target: createTargetFor(filter) }))),
    [filter, getModuleUrl, createTargetFor],
  );

  const handleEmptyCta = useCallback((): void => {
    tracking.trackEmptyCta(createDestination());
    openCreateFlow();
  }, [tracking, openCreateFlow, createDestination]);

  const handleCreateCta = useCallback((): void => {
    tracking.trackCreateCta(createDestination());
    openCreateFlow();
  }, [tracking, openCreateFlow, createDestination]);

  const handleSegmentChange = useCallback(
    (next: ActivitySegment): void => {
      tracking.trackSegmentTab(next);
      setSegment(next);
    },
    [tracking, setSegment],
  );

  const handleFilterChange = useCallback(
    (next: ActivityFilter): void => {
      tracking.trackFilterChip(next);
      setFilter(next);
    },
    [tracking, setFilter],
  );

  // A star tap is the answer. Where the building collects comments it opens the comment step and the
  // submit waits for it (feedback-service takes rating + comment in one call); otherwise it submits now.
  const handleRate = useCallback(
    (id: string, rating: number): void => {
      const item = items.find((candidate) => candidate.id === id);
      if (!item) return;

      const feedback = feedbackFor(item);
      const commentsEnabled = Boolean(feedback?.commentsEnabled);
      tracking.trackRatingStarClick({
        item,
        rating,
        commentsEnabled,
        candidateUuid: feedback?.candidateUuid,
      });

      if (commentsEnabled) {
        setPendingRating({ item, rating });

        return;
      }

      submit({ item, rating });
    },
    [items, tracking, submit, feedbackFor],
  );

  const closeCommentStep = useCallback(() => setPendingRating(null), []);

  /** Comment given: one atomic submit carrying both. */
  const handleCommentSubmit = useCallback(
    (comment: string): void => {
      if (!pendingRating) return;

      const { item, rating } = pendingRating;
      tracking.trackRatingCommentSubmit({ item, rating, candidateUuid: feedbackFor(item)?.candidateUuid });
      submit({ item, rating, comment });
      closeCommentStep();
    },
    [pendingRating, tracking, submit, closeCommentStep, feedbackFor],
  );

  /** Skipped or dismissed: the comment was optional, so keep the rating rather than losing the tap. */
  const handleCommentSkip = useCallback((): void => {
    if (!pendingRating) return;

    submit({ item: pendingRating.item, rating: pendingRating.rating });
    closeCommentStep();
  }, [pendingRating, submit, closeCommentStep]);

  // Infinite scroll: a sentinel at the list bottom pulls the active segment's next page. A short list leaves
  // the sentinel permanently on screen, so `canLoadMore` — not the scroll position — is what keeps this honest.
  const scrollRef = useRef<HTMLDivElement>(null);
  const handleLoadMore = useCallback((): void => loadMore(segment), [loadMore, segment]);
  const sentinelRef = useInfiniteScroll({
    onLoadMore: handleLoadMore,
    enabled: canLoadMore(segment),
    root: scrollRef,
  });

  // Pull down on the scroll container to refresh the active segment. There is deliberately no pull-UP
  // counterpart: infinite scroll plus its own retry-on-failure already cover loading the next page.

  // ONE state machine rather than a pair of booleans. Two sources (the query's `isRefreshing`
  // plus an effect-set "done" flag) leave renders where NEITHER holds — at release, and again when the
  // refetch settles — so the indicator collapsed and re-opened each time. That double-bounce was the
  // remaining jumpiness, and the frame where the spinner was still mounted as the message arrived is why
  // it looked like both were showing at once. A single phase means every transition is one state update.
  const [downPhase, setDownPhase] = useState<PullPhase>('idle');
  const downTimer = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => () => clearTimeout(downTimer.current), []);

  const handleRefresh = useCallback((): void => {
    tracking.trackPullRefresh();
    setDownPhase('running');
    // Settling flips straight to 'done' in a single update, so the indicator never collapses in between.
    refresh(segment).finally(() => {
      setDownPhase('done');
      clearTimeout(downTimer.current);
      downTimer.current = setTimeout(() => setDownPhase('idle'), CONFIRMATION_MS);
    });
  }, [refresh, segment, tracking]);

  const pull = usePullGesture({
    scrollRef,
    onPullDown: handleRefresh,
    canPullDown: downPhase === 'idle',
  });

  // ONE transform for the whole content block, which is what keeps this smooth: the indicator is a fixed
  // row tucked behind a negative margin, and revealing it is a compositor-only translate. Animating its
  // height instead reflowed the entire card list on every touchmove — the source of the stutter. A held
  // state (in-flight refresh, or a confirmation showing) pins the offset; otherwise the finger drives it.
  const contentOffset = downPhase === 'idle' ? pull.distance : INDICATOR_HEIGHT;
  // No transition while the finger is down — easing there IS the lag. Ease only the snap and the collapse.
  const isDragging = pull.distance > 0;

  // ONE persistently-mounted live region for every transient status, rather than announcing from the
  // messages themselves. iOS VoiceOver — the AT that actually matters here — is unreliable about reading an
  // aria-live node that gets INSERTED with its text already in place, which is exactly how the pull
  // confirmations mount. Keeping the node alive and swapping only its text is what gets announced.
  let liveStatus = '';
  if (downPhase === 'done') liveStatus = intl.formatMessage({ id: 'activity.caughtUp.title' });
  else if (hasPartialError && !isError) liveStatus = intl.formatMessage({ id: 'activity.partialError' });

  const renderBody = (): JSX.Element => {
    // Total outage: every source failed → an error state, NOT the "caught up" fake-empty feed.
    if (isError && !isFetching) {
      return (
        <EmptyStateShell
          icon={CircleAlert}
          iconClassName="text-system-pink"
          title={intl.formatMessage({ id: 'activity.error.title' })}
          body={intl.formatMessage({ id: 'activity.error.body' })}
          dataTestId="activity-feed-error"
        />
      );
    }

    // Still loading and nothing to show yet: keep the spinner rather than flashing a terminal empty
    // state before the slower sources settle. Excludes a pull-to-refresh — that has its own indicator,
    // and swapping in the full-pane spinner would yank the list out from under the user's thumb.
    if (isFetching && items.length === 0 && downPhase === 'idle' && !isRefreshing(segment)) {
      return (
        <div className="flex justify-center py-16" data-testid="activity-feed-loading">
          <Spinner />
        </div>
      );
    }

    // Vertical tabs (Requests / Bookings / Events / Visitors) get a CTA that opens that module's base
    // miniapp; the "All" tab has no single module, so no button. Applies to BOTH empty screens.
    // The building has to offer the vertical at all: with no utility button for it there is nothing to
    // create into, and the deeplink check alone only proves the miniapp URL is configured for the env.
    /*
     * Eligibility follows the DESTINATION, which differs per filter. Events open the host's events
     * page, so they ask the host — requiring a module url as well would withhold a working CTA
     * wherever that env var happens to be unset, for a url the button no longer opens. Everything
     * else opens its module's create flow, which needs the url and the building's own button.
     */
    const showCta = filter !== 'all'
      && isAvailable(filter)
      && (filter === 'event' ? canOpenHostPage : canOpenModule(filter, { target: createTargetFor(filter) }));

    /*
     * The end-of-list offer, which replaced the floating `+`.
     *
     * Same availability test as the empty state's CTA — the building must actually offer the
     * vertical — but the opposite population test: the empty state already carries this action when
     * the pane is empty, so showing both would be the same button twice. And only once the list has
     * genuinely ended; while another page can still load, this is not the bottom.
     */
    const showEndCta = (filter === 'all' ? canOpenHostPage : showCta)
      && items.length > 0
      && isAtEnd(segment);

    // One empty state per empty pane — segment-aware copy ("No upcoming …" / "No prior …") + the
    // module CTA (visitors deeplink to the create-visit form). No separate "caught up" state: an empty
    // pane reads the same whether or not the other pane has items.
    if (items.length === 0) {
      return (
        <ActivityEmptyState
          filter={filter}
          Icon={filter === 'all' ? undefined : iconFor(filter)}
          segment={segment}
          onCta={showCta ? handleEmptyCta : undefined}
        />
      );
    }

    return (
      <div className="flex flex-col gap-3" data-testid="activity-feed-list">
        {items.map((item) => {
          const hasBookAgain = !!item.actions?.find((a) => a.type === 'book_again');
          return (
            <ActivityCard
              key={item.id}
              item={item}
              Icon={iconFor(item.type)}
              tappable={isTappable(item)}
              canBookAgain={hasBookAgain && canBookAgain(item)}
              canCancel={isVisitCancelable(item, segment)}
              canCheckIn={isCheckInOpen(item) && !!checkInEventUuid(item)}
              canCancelEvent={isCancelOpen(item) && !!checkInEventUuid(item)}
              canCancelBooking={isBookingCancelOpen(item) && !!bookingTransactionUuid(item)}
              canCheckInBooking={isBookingCheckInOpen(item) && !!bookingTransactionUuid(item)}
              checkInPhase={checkInFeedback.phaseFor(item.id)}
              onOpen={handleOpen}
              onCancel={handleCancel}
              onCheckIn={handleCheckIn}
              onCancelEvent={handleCancelEvent}
              onCancelBooking={handleCancelBooking}
              onCheckInBooking={handleCheckInBooking}
              rating={ratingFor(item)}
              showRating={!!feedbackFor(item)}
              ratingReadOnly={isSubmitted(item)}
              onRate={handleRate}
              isSelected={isSplitSurface && item.id === highlightedId}
            />
          );
        })}
        {isLoadingMore(segment) && (
          <div className="flex justify-center py-4" data-testid="activity-feed-loading-more">
            <Spinner />
          </div>
        )}
        {/* Sentinel: when it scrolls into view, the active segment's next page loads. */}
        <div ref={sentinelRef} aria-hidden data-testid="activity-feed-sentinel" />
        {/*
          * Only once the list has actually ended. While another page can still arrive this is not
          * the bottom, and an offer to create something reads as the end of the records when it is
          * not. Sits after the sentinel so it cannot be what trips the next page into loading.
          */}
        {showEndCta && <ActivityEndCta filter={filter} onCta={handleCreateCta} />}
      </div>
    );
  };

  return (
    <div data-testid="activity-feed" className="relative flex h-full flex-col">
      {/* Persistent live region: the text swaps in place so iOS VoiceOver actually announces it. */}
      <div aria-live="polite" aria-atomic className="sr-only" data-testid="activity-status-live">
        {liveStatus}
      </div>
      {/*
        * Static header above the scroll area — never scrolls or bounces on overscroll.
        *
        * No top padding on the split surface: the host puts a bordered record card beside this
        * column, and 16px of air above the title left it sitting below the card's top edge with
        * nothing between them to explain the step. Everywhere else this app owns the whole viewport
        * and the padding is what keeps the title off the shell's header.
        */}
      <div
        className={cn('flex-none bg-background', !isSplitSurface && 'pt-4')}
        data-testid="activity-feed-header"
      >
        <div className={cn(contentColumn, HEADER_INSET)}>
          <h1 className="text-[28px] font-bold tracking-[-0.02em] text-foreground">
            {intl.formatMessage({ id: 'activity.title' })}
          </h1>
        </div>
        <div className={cn(contentColumn, HEADER_INSET, 'pt-4')}>
          {/* Upcoming only, matching the design. The count is the whole upcoming total across all
              types — deliberately not narrowed by the active filter chip or the current tab. */}
          <ActivitySegmented
            value={segment}
            onChange={handleSegmentChange}
            counts={{ upcoming: upcomingTotal }}
          />
        </div>
        <div className={cn(contentColumn, HEADER_INSET, 'pb-3 pt-[18px]')}>
          <ActivityFilterChips active={filter} onChange={handleFilterChange} />
        </div>
      </div>
      {/* The card list is the scroll container; overscroll-y-contain keeps its bounce. `overflow-x-hidden`
          because a `visible` axis computes to `auto` once the other one is not — so `overflow-y-auto`
          alone left this scrolling sideways under a trackpad swipe. The filter rail keeps its own
          `overflow-x-auto`, which is a rail that is meant to scroll. */}
      <div
        ref={scrollRef}
        // Bottom padding is moderate by default so the list runs to the bottom of the container; it only
        // opens up to clear the visitor FAB, which floats over the last card when that pane is active.
        className={cn(
          'min-h-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-y-contain pt-1',
          'pb-5',
        )}
        data-testid="activity-feed-scroll"
        onTouchStart={pull.handlers.onTouchStart}
        onTouchMove={pull.handlers.onTouchMove}
        onTouchEnd={pull.handlers.onTouchEnd}
        onTouchCancel={pull.handlers.onTouchCancel}
      >
        {/* One translated block: the indicators are fixed rows tucked behind negative margins, so revealing
            them is a single compositor-only transform rather than a per-frame relayout of the card list. */}
        <div
          className={cn(contentColumn, LIST_INSET)}
          style={{
            transform: `translateY(${contentOffset}px)`,
            transition: isDragging ? undefined : 'transform 220ms cubic-bezier(0.22, 1, 0.36, 1)',
          }}
        >
          {/* Pull-DOWN: refresh the active segment, then confirm it. */}
          <PullIndicator
            distance={pull.distance}
            active={downPhase !== 'idle'}
            message={downPhase === 'done' ? intl.formatMessage({ id: 'activity.caughtUp.title' }) : undefined}
            dataTestId="activity-pull-refresh"
          />
          {/* Some (not all) sources failed → render what loaded, but say so rather than silently under-showing. */}
          {hasPartialError && !isError && (
            <div
              className="mx-0 mb-3 flex items-center gap-2 rounded-xl bg-system-grey-light px-3.5 py-2.5 text-sm text-system-grey-dark"
              // No `role="status"` — this banner is also conditionally mounted WITH its text, the pattern
              // iOS VoiceOver skips. The persistent live region above announces it instead; keeping the
              // role here as well would double-announce on the platforms that do handle it.
              data-testid="activity-feed-partial-error"
            >
              <CircleAlert size={16} strokeWidth={2} className="shrink-0 text-system-grey-dark" aria-hidden />
              {intl.formatMessage({ id: 'activity.partialError' })}
            </div>
          )}
          {renderBody()}
        </div>
      </div>
      <RatingCommentModal
        isOpen={!!pendingRating}
        rating={pendingRating?.rating ?? 0}
        onSubmit={handleCommentSubmit}
        onSkip={handleCommentSkip}
      />
      <VisitCancelModal
        isOpen={!!cancelItem}
        visitorName={cancelItem?.details.title ?? ''}
        when={cancelWhen}
        isCancelling={cancelMutation.isPending}
        isError={cancelMutation.isError}
        onConfirm={confirmCancel}
        onClose={closeCancel}
      />

      <EventCancelModal
        isOpen={!!cancelEventItem}
        eventName={cancelEventItem?.details.title ?? ''}
        when={modalWhen(cancelEventItem)}
        isCancelling={cancelEventMutation.isPending}
        isError={cancelEventMutation.isError}
        onConfirm={confirmCancelEvent}
        onClose={closeCancelEvent}
      />

      <BookingCancelModal
        isOpen={!!cancelBookingItem}
        bookingName={cancelBookingItem?.details.title ?? ''}
        when={modalWhen(cancelBookingItem)}
        isCancelling={cancelBookingMutation.isPending}
        isError={cancelBookingMutation.isError}
        onConfirm={confirmCancelBooking}
        onClose={closeCancelBooking}
      />
    </div>
  );
};
